/**
 * Table des demandes : triable par en-tête, avec appel et email accessibles
 * en un clic depuis la ligne.
 *
 * Le tri se fait dans le navigateur et non par l'API : la liste tient en
 * mémoire, et un aller-retour réseau à chaque clic sur un en-tête donnerait
 * l'impression que la table hésite.
 */
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link as LienRoutage, useNavigate } from "react-router";
import {
  LIBELLES_BUDGET,
  LIBELLES_PROJET,
  type Lead,
  formaterDate,
  formaterTelephone,
  libelle,
  versDate,
} from "./LeadsDonnees";
import { PuceStatut } from "./LeadsStatut";

export type ColonneTri = "date" | "nom" | "commune" | "projet" | "statut";

export type Tri = {
  colonne: ColonneTri;
  sens: "asc" | "desc";
};

export const TRI_PAR_DEFAUT: Tri = { colonne: "date", sens: "desc" };

const COLONNES: { cle: ColonneTri; libelle: string }[] = [
  { cle: "date", libelle: "Reçue le" },
  { cle: "nom", libelle: "Nom" },
  { cle: "commune", libelle: "Commune" },
  { cle: "projet", libelle: "Projet" },
  { cle: "statut", libelle: "Statut" },
];

/** Valeur comparable d'une demande pour une colonne donnée. */
function cle(lead: Lead, colonne: ColonneTri): string | number {
  switch (colonne) {
    case "date":
      return versDate(lead.createdAt)?.getTime() ?? 0;
    case "nom":
      return lead.nom;
    case "commune":
      return lead.communeTexte;
    case "projet":
      return libelle(LIBELLES_PROJET, lead.typeProjet);
    case "statut":
      return lead.statut;
  }
}

/**
 * Tri stable, accents compris.
 *
 * `localeCompare` en français classe « Émile » entre « Emile » et « Fabre »
 * là où une comparaison sur les points de code le rejetterait après « Zoé ».
 */
export function trierLeads(leads: Lead[], tri: Tri): Lead[] {
  const facteur = tri.sens === "asc" ? 1 : -1;

  return [...leads].sort((gauche, droite) => {
    const a = cle(gauche, tri.colonne);
    const b = cle(droite, tri.colonne);

    const ecart =
      typeof a === "number" && typeof b === "number"
        ? a - b
        : String(a).localeCompare(String(b), "fr", { sensitivity: "base" });

    // À égalité, l'ordre d'arrivée départage : sans cela, deux demandes de la
    // même commune changeraient de place à chaque rendu.
    return ecart !== 0 ? ecart * facteur : droite.id - gauche.id;
  });
}

type Proprietes = {
  leads: Lead[];
  tri: Tri;
  surTri: (tri: Tri) => void;
};

export function LeadsTable({ leads, tri, surTri }: Proprietes) {
  const naviguer = useNavigate();

  function basculer(colonne: ColonneTri) {
    if (tri.colonne === colonne) {
      surTri({ colonne, sens: tri.sens === "asc" ? "desc" : "asc" });
      return;
    }
    // Une nouvelle colonne part du sens le plus utile : le plus récent d'abord
    // pour une date, l'ordre alphabétique pour un texte.
    surTri({ colonne, sens: colonne === "date" ? "desc" : "asc" });
  }

  if (leads.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Aucune demande ne correspond à ces critères.</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label="Demandes de devis">
        <TableHead>
          <TableRow>
            {COLONNES.map((colonne) => (
              <TableCell
                key={colonne.cle}
                sortDirection={tri.colonne === colonne.cle ? tri.sens : false}
              >
                <TableSortLabel
                  active={tri.colonne === colonne.cle}
                  direction={tri.colonne === colonne.cle ? tri.sens : "asc"}
                  onClick={() => basculer(colonne.cle)}
                >
                  {colonne.libelle}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Budget</TableCell>
            <TableCell align="right">Contact</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {leads.map((lead) => (
            <TableRow
              key={lead.id}
              hover
              onClick={() => naviguer(`/leads/${lead.id}`)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell sx={{ whiteSpace: "nowrap" }}>{formaterDate(lead.createdAt)}</TableCell>

              <TableCell>
                {/* Un vrai lien, et pas seulement une ligne cliquable : la
                    tabulation doit atteindre la fiche, et le clic du milieu
                    l'ouvrir dans un onglet. */}
                <Link
                  component={LienRoutage}
                  to={`/leads/${lead.id}`}
                  onClick={(evenement) => evenement.stopPropagation()}
                  underline="hover"
                  sx={{ fontWeight: 500 }}
                >
                  {lead.nom}
                </Link>
              </TableCell>

              <TableCell>{lead.communeTexte}</TableCell>

              <TableCell>
                {libelle(LIBELLES_PROJET, lead.typeProjet)}
                {lead.surface !== null && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {` — ${lead.surface} m²`}
                  </Typography>
                )}
              </TableCell>

              <TableCell>
                <PuceStatut statut={lead.statut} />
              </TableCell>

              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {libelle(LIBELLES_BUDGET, lead.budget)}
              </TableCell>

              <TableCell align="right">
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Tooltip title={`Appeler ${formaterTelephone(lead.telephone)}`}>
                    <IconButton
                      size="small"
                      component="a"
                      href={`tel:${lead.telephone}`}
                      aria-label={`Appeler ${lead.nom} au ${formaterTelephone(lead.telephone)}`}
                      onClick={(evenement) => evenement.stopPropagation()}
                    >
                      <PhoneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={lead.email}>
                    <IconButton
                      size="small"
                      component="a"
                      href={`mailto:${lead.email}`}
                      aria-label={`Écrire à ${lead.nom}`}
                      onClick={(evenement) => evenement.stopPropagation()}
                    >
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
